import { useState } from 'preact/hooks';
import { read } from '../api/client.js';
import { taskResourceKey, useResource } from '../api/resource.js';
import { useSpaceContext } from '../api/space-context.js';
import { SpaceContextAdoption } from './SpaceContextAdoption.jsx';
import { useSession } from '../session/session.jsx';
import { Button, ButtonLink, Chip } from '../ui/primitives.jsx';
import { Empty, Resource } from '../ui/states.jsx';
import './task-reentry.css';

const PAGE_SIZE = 10;
const roles = { owner: '오너', participant: '참여자', observer: '관찰자', none: '참여 역할 없음' };
const requestStates = { open: '열림', offered: '수락 대기', accepted: '넘김 수락됨' };
const verdicts = [
  ['canContribute', '기여'], ['canAcceptAsHumanDirect', '사람 직접 수락'], ['canManageParticipants', '참여자 관리'],
];

export function TaskReentryPane({ spaceId, taskId }) {
  const session = useSession();
  if (!spaceId || !taskId || !session.nodeId || session.status !== 'ready') {
    return <Empty title="읽을 Task와 참여 Node를 선택해 주세요." />;
  }
  // 페이지 위치도 신원·Task에 귀속된다. 바뀐 범위에 이전 커서를 보내지 않는다.
  return <Reentry key={JSON.stringify([session.nodeId, spaceId, taskId])}
    nodeId={session.nodeId} spaceId={spaceId} taskId={taskId} />;
}

function Reentry({ nodeId, spaceId, taskId }) {
  const context = useSpaceContext(spaceId, nodeId);
  const [page, setPage] = useState({ cursor: null, number: 1 });
  const resource = useResource(taskResourceKey(spaceId, taskId, `reentry:${page.cursor ?? 'first'}`), async (signal) => {
    const data = await read('task-reentry', { nodeId, signal,
      query: { spaceId, taskId, limit: PAGE_SIZE, ...(page.cursor ? { cursor: page.cursor } : {}) } });
    if (data?.schemaVersion !== 1 || data.spaceId !== spaceId || data.taskId !== taskId) {
      throw new Error('선택한 Task의 맥락을 확인하지 못했습니다.');
    }
    return data;
  }, { quiet: true, identity: nodeId });
  const detail = `/s/${spaceId}/task/${taskId}`;

  return <section class="work work--fluid task-reentry">
    <header class="work__head">
      <div class="work__head-text"><h1 class="t-view">맥락부터 읽기</h1>
        <p class="t-meta">목적과 수락된 이해를 확인하고, 필요한 논의로 이어갑니다.</p></div>
    </header>
    <div class="work__body">
      <Resource resource={resource} loadingLabel="Task 맥락을 읽는 중">{(data) => <>
        <div class="task-reentry__heading">
          <h2 class="t-section">{data.title}</h2>
          <ButtonLink size="sm" href={detail}>전체 Task 상세 읽기</ButtonLink>
        </div>
        <p class="t-meta">전체 상세를 열면 이 Task의 전체 판단과 참조 목록을 불러옵니다.</p>
        <section class="task-reentry__section" aria-labelledby="reentry-version">
          <h3 id="reentry-version" class="t-section">수락된 공동 이해</h3>
          {data.currentVersion === null ? <p class="t-body">아직 수락된 공동 이해가 없습니다.</p> : <>
            <Chip tone="accepted">v{data.currentVersion.versionNo} · 수락된 현재 판</Chip>
            <div class="task-reentry__prefix">
              <h4 class="t-body-strong">목적</h4>
              <p class="t-body task-reentry__text">{data.currentVersion.purpose}</p>
            </div>
            <Prefix label="확정한 사실" value={data.currentVersion.confirmed} detail={detail} />
            <Prefix label="아직 모르는 것" value={data.currentVersion.openQuestions} detail={detail} />
            <Prefix label="참조 ID" value={data.currentVersion.referenceIds} detail={detail} ids />
            <details class="task-reentry__details">
              <summary class="t-meta">수락된 이해의 출처 보기</summary>
              <p class="t-meta">승격 출처 제안: <span class="t-mono">{data.currentVersion.promotedFromProposalId}</span></p>
            </details>
            <SpaceContextAdoption spaceId={spaceId} nodeId={nodeId} taskId={taskId} task={resource} context={context} />
          </>}
        </section>
        <details class="task-reentry__details task-reentry__intent">
          <summary class="t-meta">Task의 초기 의도 · 수락된 이해 아님</summary>
          <p class="t-body task-reentry__text">{data.seedPurpose ?? '기록된 초기 의도가 없습니다.'}</p>
        </details>
        <section class="task-reentry__section" aria-labelledby="reentry-permissions">
          <div class="task-reentry__heading">
            <h3 id="reentry-permissions" class="t-body-strong">현재 역할과 권한</h3>
            <p class="t-meta">Task 역할: {roles[data.permissions.taskRole] ?? data.permissions.taskRole}</p>
          </div>
          <dl class="task-reentry__verdicts">{verdicts.map(([key, label]) => <div key={key}>
            <dt class="t-meta">{label}</dt><dd class="t-meta">{data.permissions[key].ok ? '허용' : '허용되지 않음'}</dd>
          </div>)}</dl>
          <p class="t-meta">이 응답 시점의 판정이며 다음 행동의 권한을 보장하지 않습니다.</p>
          {resource.isStale ? <p class="t-meta">최신 판정을 다시 확인하는 중입니다.</p> : null}
          <details class="task-reentry__details">
            <summary class="t-meta">서버 판정 근거 보기</summary>
            <dl class="task-reentry__reasons">{verdicts.map(([key, label]) => <div key={key}>
              <dt class="t-meta">{label}</dt><dd class="t-meta">
                {data.permissions[key].reason ? <span class="t-mono">{data.permissions[key].reason}</span> : '별도 사유 코드 없음'}
              </dd>
            </div>)}</dl>
          </details>
        </section>
        <section class="task-reentry__section" aria-labelledby="reentry-pointers">
          <h3 id="reentry-pointers" class="t-section">열린 요청</h3>
          <p class="t-meta">열린 제안은 수락된 사실이 아닙니다. 여기서는 요청의 종류·상태·ID만 읽습니다.</p>
          <p class="t-meta">{page.number}페이지 · {data.pointers.items.length}건 · 한 번에 최대 {PAGE_SIZE}건{data.pointers.hasMore ? ' · 다음 페이지 있음' : ' · 마지막 페이지'}</p>
          {data.pointers.items.length ? <ul class="task-reentry__pointers">{data.pointers.items.map((pointer) => <li key={`${pointer.kind}:${pointer.id}`}>
            <span class="t-body-strong">{({ question: '미답 질문', handoff: '넘김 요청', proposal: '열린 제안 · 미수락' })[pointer.kind]}</span>
            <span class="t-meta">{requestStates[pointer.state] ?? '상태 확인 필요'}</span>
            <details class="task-reentry__details">
              <summary class="t-meta">요청 ID 보기</summary><span class="t-mono">{pointer.id}</span>
            </details>
          </li>)}</ul> : <p class="t-body">이 페이지에 열린 요청이 없습니다.</p>}
          <nav class="row" aria-label="열린 요청 페이지">
            <Button size="sm" disabled={page.cursor === null} onClick={() => setPage({ cursor: null, number: 1 })}>첫 페이지</Button>
            <Button size="sm" disabled={resource.isStale || !data.pointers.hasMore || !data.pointers.nextCursor || data.pointers.nextCursor === page.cursor}
              onClick={() => setPage({ cursor: data.pointers.nextCursor, number: page.number + 1 })}>다음 페이지</Button>
            <ButtonLink size="xs" tone="ghost" href={detail}>요청 내용은 전체 Task 상세에서 읽기</ButtonLink>
          </nav>
        </section>
      </>}</Resource>
    </div>
  </section>;
}

function Prefix({ label, value, detail, ids = false }) {
  return <div class="task-reentry__prefix">
    <h4 class="t-body-strong">{label}</h4>
    <p class="t-meta">전체 {value.total}건 중 {value.items.length}건 표시{value.incomplete ? ' · 일부만 표시 (불완전)' : ''}</p>
    {value.items.length ? <ul>{value.items.map((item, index) => <li key={ids ? item : item.id ?? index}
      class={ids ? 't-mono' : 't-body task-reentry__text'}>{ids ? item : item.text}</li>)}</ul> : <p class="t-meta">표시할 항목이 없습니다.</p>}
    {value.incomplete ? <ButtonLink size="xs" tone="ghost" href={detail}>{label} 전체는 Task 상세에서 읽기</ButtonLink> : null}
  </div>;
}
